/**
 * Multiple-birth markers for dashboard lists (aligned with Heads of Line / Color).
 *
 * A set is recorded two ways in the payload, and a member may carry either one:
 *   - multiple_births_type — the set size (2-9), set on the member the form was
 *     filled from;
 *   - multiple_birth_details — the links to the rest of the set, which is all the
 *     other members of that set carry.
 * Reading only the first missed the siblings of an anchor member — the second twin
 * in a set showed no tag, so the pair read as one lone twin. The family tree groups
 * by the links for the same reason (see identifyTwinsTripletsSets in
 * Components/FamilyTree/familyTreeUtils.js), so the dashboards now agree with it.
 */

/** Set size → row tag, matching the tree node badges (Twin/Trip/Quad/Quin/…). */
const MULTIPLE_BIRTH_TAG_BY_SIZE = {
  2: "twin",
  3: "trip",
  4: "quad",
  5: "quin",
  6: "sex",
  7: "sep",
  8: "oc",
  9: "non",
};

/** Word forms the type field can arrive as, e.g. "Twins (2)" or "triplets". */
const MULTIPLE_BIRTH_SIZE_BY_WORD = [
  [/\bnon|\bnun/, 9],
  [/\boct|\bocs?\b/, 8],
  [/\bsept|\bseps?\b/, 7],
  [/\bsext|\bsexs?\b/, 6],
  [/\bquint|\bquins?\b/, 5],
  [/\bquad/, 4],
  [/\btrip/, 3],
  [/\btwin/, 2],
];

/** The set size a raw multiple_births_type stands for, or null if it names none. */
function parseMultipleBirthsType(raw) {
  if (raw === undefined || raw === null || raw === "") return null;

  const text = String(raw).trim().toLowerCase();
  if (text === "" || text === "null" || text === "undefined" || text === "0") {
    return null;
  }

  // "3" and "Triplets (3)" alike — the digit is the set size wherever it sits.
  const digit = text.match(/[2-9]/);
  if (digit) return Number(digit[0]);

  const word = MULTIPLE_BIRTH_SIZE_BY_WORD.find(([pattern]) => pattern.test(text));
  return word ? word[1] : null;
}

/** multiple_birth_details as an array, whether it arrives as one or as JSON text. */
function parseMultipleBirthDetailsArray(details) {
  if (Array.isArray(details)) return details;
  if (typeof details !== "string" || details.trim() === "") return [];
  try {
    const parsed = JSON.parse(details);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

/** Every id a row identifies itself by, so its own entry never counts as a sibling. */
function getMemberOwnIds(member) {
  return [
    member?.id,
    member?.family_member_id,
    member?.member_id,
    member?.user_id,
  ]
    .filter((id) => id !== undefined && id !== null && id !== "")
    .map(String);
}

/**
 * How many others this member is linked to as a multiple birth.
 * Mirrors parseMultipleBirthDetails in familyTreeUtils.js, including the
 * misspelled `famiy_member_id` the API sends on some rows.
 */
function countLinkedMultipleBirthSiblings(member) {
  const entries = [
    ...parseMultipleBirthDetailsArray(member?.multiple_birth_details),
    ...parseMultipleBirthDetailsArray(member?.multiple_births_details),
  ];
  if (entries.length === 0) return 0;

  const ownIds = new Set(getMemberOwnIds(member));
  const siblingIds = new Set();

  entries.forEach((entry) => {
    const rawId =
      entry?.family_member_id ?? entry?.famiy_member_id ?? entry?.member_id;
    if (rawId === undefined || rawId === null || rawId === "") return;
    const id = String(rawId);
    if (ownIds.has(id)) return;
    siblingIds.add(id);
  });

  return siblingIds.size;
}

/** The nested object shape some endpoints wrap the type in. */
function getNestedMultipleBirthsType(member) {
  const nestedDetails = member?.multiple_births_details;
  if (
    nestedDetails &&
    typeof nestedDetails === "object" &&
    !Array.isArray(nestedDetails)
  ) {
    return nestedDetails.multiple_births_type;
  }

  const details = member?.multiple_birth_details;
  if (details && typeof details === "object" && !Array.isArray(details)) {
    return details.multiple_births_type;
  }

  return undefined;
}

/**
 * The set size the member's own record states, under any of the names the
 * endpoints use: multiple_births_type on member records, multiple_birth_type and
 * badge ("twin"/"trip") on the statistics multiple_births section, and the older
 * twin field the tree nodes carry.
 */
function getOwnMultipleBirthTypeSize(member) {
  return [
    member?.multiple_births_type ?? getNestedMultipleBirthsType(member),
    member?.multiple_birth_type,
    member?.badge,
    member?.twin,
  ].reduce((size, raw) => size || parseMultipleBirthsType(raw), null);
}

/**
 * The size of the multiple-birth set this member belongs to, 2-9, or null.
 * @param {object} member
 * @returns {number|null}
 */
export function getMultipleBirthSetSize(member) {
  if (!member) return null;

  const fromType = getOwnMultipleBirthTypeSize(member);
  if (fromType) return fromType;

  // No type of its own: the member is a sibling in someone else's set, so the
  // links say how big it is — the others plus this member.
  const linked = countLinkedMultipleBirthSiblings(member);
  if (linked >= 1) return Math.min(linked + 1, 9);

  return null;
}

/**
 * Multiple-birth superscript label for dashboard lists.
 * @param {object} member - Family member or anniversary row (primary fields on root).
 * @returns {"twin"|"trip"|"quad"|"quin"|"sex"|"sep"|"oc"|"non"|null}
 */
export function getMultipleBirthSuperscriptText(member) {
  return getMultipleBirthTagForSize(getMultipleBirthSetSize(member));
}

/** The row tag for a set size, or null. */
export function getMultipleBirthTagForSize(size) {
  return size ? MULTIPLE_BIRTH_TAG_BY_SIZE[size] || null : null;
}

/**
 * The multiple-birth set size of every member in a list, by member id, read
 * across the whole list rather than one record at a time.
 *
 * A twin whose own record carries neither the type nor any links is still in
 * the set when a sibling's record links to them — the family tree draws them
 * that way (identifyTwinsTripletsSets links both directions), so a list that
 * only read each member's own record dropped the tag on one or both of the
 * pair. Records linked to one another make one set; its size is the larger
 * of how many members it links and any type a member of it carries.
 *
 * @param {object[]} members - Every record the list knows of; the same member
 *   may appear more than once (roster record and tree node).
 * @returns {Map<string, number>} member id → set size (2-9)
 */
export function buildMultipleBirthSetSizes(members) {
  const parent = new Map();
  const find = (id) => {
    if (!parent.has(id)) parent.set(id, id);
    let root = id;
    while (parent.get(root) !== root) root = parent.get(root);
    parent.set(id, root);
    return root;
  };
  const union = (a, b) => {
    const rootA = find(a);
    const rootB = find(b);
    if (rootA !== rootB) parent.set(rootA, rootB);
  };

  const typeSizeById = new Map();
  const linkedIds = new Set();
  const list = Array.isArray(members) ? members : [];

  // multiple_birth_member_user_id names the other twin by their user id, so
  // every id a record goes by resolves to its member id.
  const memberIdByAlias = new Map();
  list.forEach((member) => {
    const [ownId, ...aliases] = getMemberOwnIds(member);
    if (!ownId) return;
    aliases.forEach((alias) => {
      if (!memberIdByAlias.has(alias)) memberIdByAlias.set(alias, ownId);
    });
  });

  list.forEach((member) => {
    const [ownId] = getMemberOwnIds(member);
    if (!ownId) return;

    const typeSize = getOwnMultipleBirthTypeSize(member);
    if (typeSize) {
      typeSizeById.set(ownId, Math.max(typeSizeById.get(ownId) ?? 0, typeSize));
    }

    [
      ...parseMultipleBirthDetailsArray(member?.multiple_birth_details),
      ...parseMultipleBirthDetailsArray(member?.multiple_births_details),
      { member_id: member?.multiple_birth_member_user_id },
    ].forEach((entry) => {
      const rawId =
        entry?.family_member_id ?? entry?.famiy_member_id ?? entry?.member_id;
      if (rawId === undefined || rawId === null || rawId === "") return;
      const id = memberIdByAlias.get(String(rawId)) ?? String(rawId);
      if (id === ownId) return;
      linkedIds.add(ownId);
      linkedIds.add(id);
      union(ownId, id);
    });
  });

  const membersBySet = new Map();
  linkedIds.forEach((id) => {
    const root = find(id);
    if (!membersBySet.has(root)) membersBySet.set(root, []);
    membersBySet.get(root).push(id);
  });

  const sizes = new Map();
  membersBySet.forEach((ids) => {
    const typeSize = Math.max(0, ...ids.map((id) => typeSizeById.get(id) ?? 0));
    const size = Math.min(Math.max(ids.length, typeSize), 9);
    ids.forEach((id) => sizes.set(id, size));
  });
  typeSizeById.forEach((size, id) => {
    if (!sizes.has(id)) sizes.set(id, size);
  });

  return sizes;
}

/**
 * Legend labels for the row tags, keyed by the value
 * getMultipleBirthSuperscriptText returns.
 */
const MULTIPLE_BIRTH_LEGEND_LABELS = {
  twin: "Twin",
  trip: "Trip",
  quad: "Quad",
  quin: "Quin",
  sex: "Sex",
  sep: "Sep",
  oc: "Oc",
  non: "Non",
};

/** Smallest set first, so the key reads in set size order, not row order. */
const MULTIPLE_BIRTH_LEGEND_ORDER = [
  "twin",
  "trip",
  "quad",
  "quin",
  "sex",
  "sep",
  "oc",
  "non",
];

/**
 * The multiple-birth labels to key in a dashboard legend for the rows on screen.
 *
 * The legend keys only the tags a list actually carries — a page with twins and
 * triplets reads "Twin Trip", and one with no multiple births at all returns []
 * so the entry drops out entirely rather than pointing at a marker nobody sees.
 *
 * @param {*} members - A row, an array of rows, or nested arrays of them
 *   (male/female, grouped memorials and the home dashboard each render more
 *   than one list under a single footer).
 * @param {function} [getTag] - Reads a row's tag; defaults to the row's own record.
 * @returns {string[]} e.g. ["Twin", "Trip"]
 */
export function getMultipleBirthLegendTags(
  members,
  getTag = getMultipleBirthSuperscriptText
) {
  const present = new Set();

  const walk = (value) => {
    if (!value) return;
    if (Array.isArray(value)) {
      value.forEach(walk);
      return;
    }
    const tag = getTag(value);
    if (tag) present.add(tag);
  };

  walk(members);

  return MULTIPLE_BIRTH_LEGEND_ORDER.filter((tag) => present.has(tag)).map(
    (tag) => MULTIPLE_BIRTH_LEGEND_LABELS[tag]
  );
}

/** Spouse slice on anniversary rows when API sends prefixed fields */
export function getSpouseMultipleBirthSlice(anniversary) {
  return {
    multiple_births_type:
      anniversary?.spouse_multiple_births_type ??
      anniversary?.spouseMultipleBirthsType,
    multiple_birth_details:
      anniversary?.spouse_multiple_birth_details ??
      anniversary?.spouseMultipleBirthDetails,
    multiple_births_details:
      anniversary?.spouse_multiple_births_details ??
      anniversary?.spouseMultipleBirthsDetails,
  };
}
